import { NgComponentOutlet } from '@angular/common';
import { Component, DestroyRef, OnInit, Type, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { HARNESS_CASES } from './harness-cases';

const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'nl'];
const TRANSLATION_RELEASE_INTERVAL = 200;

/**
 * Monté sur `/e2e-harness/:caseId`. `data-testid="harness-ready"` est le signal attendu par les specs.
 * État en signals : les apps consommatrices peuvent tourner en zoneless + OnPush.
 */
@Component({
  imports: [NgComponentOutlet],
  selector: 'e2e-harness',
  template: `
    @if (this.component(); as component) {
      <div data-testid="harness-ready">
        <ng-container *ngComponentOutlet="component" />
      </div>
    } @else {
      <div data-testid="harness-unknown-case">Unknown case: {{ this.caseId() }}</div>
    }
  `,
})
export class HarnessComponent implements OnInit {
  public readonly caseId = signal('');
  public readonly component = signal<Type<unknown> | null>(null);

  private readonly _cases = inject(HARNESS_CASES, { optional: true }) ?? [];
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _route = inject(ActivatedRoute);
  private readonly _translate = inject(TranslateService);

  public ngOnInit(): void {
    SUPPORTED_LANGUAGES.forEach(lang => this._translate.setTranslation(lang, {}, true));

    // `blockExternal` stubbe Strapi : sans ce release manuel, ngx-translate reste pending pour toujours.
    const interval = setInterval(() => this._releaseStuckTranslations(), TRANSLATION_RELEASE_INTERVAL);
    this._destroyRef.onDestroy(() => clearInterval(interval));

    this._route.paramMap.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(params => {
      const caseId = params.get('caseId') ?? '';
      this.caseId.set(caseId);
      this.component.set(this._cases.find(item => item.id === caseId)?.component ?? null);
    });
  }

  private _releaseStuckTranslations(): void {
    const translate = this._translate as unknown as {
      currentLang: string;
      onTranslationChange: { emit: (event: { lang: string; translations: object }) => void };
      pending: boolean;
      translations: Record<string, object>;
    };
    if (!translate.pending) {
      return;
    }
    translate.pending = false;
    const lang = translate.currentLang ?? 'fr';
    translate.onTranslationChange.emit({ lang, translations: translate.translations[lang] ?? {} });
  }
}
